import React from 'react';
import { AppBar, Toolbar, Box, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { MenuItemLink, RefreshIconButton, useTranslate } from 'react-admin';
import RevloopIcon from '@/assets/icons/revloop.svg?react';
import MaasloopIcon from '@/assets/icons/maasloop.svg?react';
import HamburgerIcon from '@/assets/icons/hamburger-icon.svg?react';
import ShortcutsIcon from '@/assets/icons/shortcuts-icon.svg?react';
import CustomUserMenu from '@/components/global/menu/CustomUserMenu';
import NotificationButton from '@/components/global/menu/NotificationButton.jsx';
import { Palette } from '@/themes/themeOverrides/Palette';

export const CustomAppBar = ({ toggleSidebar, isSidebarOpen }) => {
    const translate = useTranslate();

    return (
        <StyledAppBar position='static'>
            <Toolbar>
                <Box className='logo'>
                    <RevloopIcon className='revloop-icon' />
                    <MenuItemLink to='/' className='logo-maasloop'>
                        <MaasloopIcon className='maasloop-icon' />
                    </MenuItemLink>
                </Box>
                <Box className='icon-container'>
                    <Box className='left-buttons'>
                        <IconButton className='shortcuts'>
                            <ShortcutsIcon />
                            <Box component='span' sx={{ typography: 'body2' }}>
                                {translate('menu.shortcuts')}
                            </Box>
                        </IconButton>
                    </Box>
                    <Box className='right-buttons'>
                        <NotificationButton />
                        <RefreshIconButton className='refresh-icon' />
                        <CustomUserMenu />
                        <IconButton onClick={toggleSidebar} className={`hamburger-icon ${isSidebarOpen ? 'active' : ''}`}>
                            <HamburgerIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Toolbar>
        </StyledAppBar>
    );
};

const StyledAppBar = styled(AppBar)(({ theme }) => ({
    backgroundColor: Palette.white,
    marginBottom: 0,
    height: '60px',
    borderBottom: `2px solid ${Palette.gray.gray100}`,
    boxShadow: 'none',
    paddingInline: '20px',
    justifyContent: 'center',

    [theme.breakpoints.up('md')]: {
        height: '84px',
        paddingInline: '32px',
    },

    '& .MuiToolbar-root': {
        padding: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: '100%',
    },

    '& .logo': {
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        width: 'auto',
        '& svg': {
            height: theme.spacing(6),
        },
        '& .logo-maasloop': {
            '&:hover': {
                background: 'transparent',
            },
        },
        [theme.breakpoints.up('md')]: {
            width: '230px',
            borderRight: `2px solid ${Palette.gray.gray100}`,
        },
    },

    '& .icon-container': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        [theme.breakpoints.up('md')]: {
            flex: 1,
            marginLeft: '20px',
        },
        [theme.breakpoints.up('lg')]: {
            marginLeft: '40px',
        },
    },

    '& .left-buttons': {
        display: 'flex',
        alignItems: 'center',
    },

    '& .right-buttons': {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        '& .MuiIconButton-root': {
            '& .MuiBadge-badge': {
                top: '-9px',
                right: '-13px',
                borderRadius: '4px',
                fontSize: '10px',
                height: '16px',
                lineHeight: '16px',
                minWidth: '16px',
                fontWeight: '700',
                padding: '0 4px',
                letterSpacing: '0.2px',
                [theme.breakpoints.down('md')]: {
                    top: '-5px',
                    right: '0px',
                },
            },
        },
        '& .notification-icon': {
            '&:focus': {
                outline: 'none',
                outlineOffset: '-2px',
                boxShadow: 'none',
            },
            '&.active': {
                outline: `2px solid ${Palette.gray.gray900}`,
                outlineOffset: '-2px',
                boxShadow: `0 0 0 2px ${Palette.yellow.yellow400}`,
            },
            '& svg': {
                position: 'relative',
                top: '-2px',
                [theme.breakpoints.down('md')]: {
                    top: '0px',
                },
            },
        },
    },

    '& .MuiIconButton-root': {
        display: 'block',
        width: '44px',
        height: '36px',
        padding: 0,
        margin: 0,
        backgroundColor: Palette.gray.gray100,
        borderRadius: '10px',
        [theme.breakpoints.up('md')]: {
            width: '52px',
            height: '44px',
        },
        '&.Mui-selected, &:focus, &.active': {
            outline: `2px solid ${Palette.gray.gray900}`,
            outlineOffset: '-2px',
            boxShadow: `0 0 0 2px ${Palette.yellow.yellow400}`,
        },
    },

    '& .hamburger-icon': {
        display: 'flex',
        [theme.breakpoints.up('md')]: {
            display: 'none',
        },
    },

    '& .refresh-icon': {
        display: 'none',
        [theme.breakpoints.up('md')]: {
            display: 'flex',
        },
    },

    '& .revloop-icon': {
        width: '50px',
        [theme.breakpoints.up('md')]: {
            width: '61px',
        },
    },

    '& .maasloop-icon': {
        width: '47px',
        [theme.breakpoints.up('md')]: {
            width: '57px',
        },
    },

    '& .RaUserMenu-userButton': {
        '& > span:not(.MuiButton-startIcon):not(.MuiTouchRipple-root)': {
            display: 'none',
            [theme.breakpoints.up('md')]: {
                display: 'inline-block',
            },
        },
    },

    '& .shortcuts': {
        display: 'none',
        [theme.breakpoints.up('md')]: {
            display: 'flex',
            gap: '8px',
            width: 'auto',
            padding: '12px 16px',
            color: Palette.black,
            '& span': {
                fontSize: '14px',
                fontWeight: '500',
            },
        },
    },
}));
